/**
 * View component for /content-modules/:contentModuleId/update
 *
 * Updates a single contentModule from a copy of the selcted contentModule
 * as defined in the contentModule reducer
 */

// import primary libraries
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { history, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// import third-party libraries
import _ from 'lodash';

// import actions
import * as certificateTemplateActions from '../../certificateTemplate/certificateTemplateActions';
import * as sponsorActions from '../../sponsor/sponsorActions';
import * as studyActions from '../../study/studyActions';
import * as contentModuleActions from '../contentModuleActions';
import * as evaluationActions from '../../evaluation/evaluationActions'; 

// import global components
import AlertModal from "../../../global/components/modals/AlertModal.js.jsx";
import AuthoringLayout from '../../admin/components/AuthoringLayout.js.jsx';
import Base from "../../../global/components/BaseComponent.js.jsx";
import Breadcrumbs from '../../../global/components/routing/Breadcrumbs.js.jsx';

// import module components
import ContentModuleForm from '../components/ContentModuleForm.js.jsx';

// import contentModule css modules
import contentModuleStyles from '../contentModuleModuleStyles.css';

class UpdateContentModule extends Base {
  constructor(props) {
    super(props);
    const { selectedContentModule, contentModuleMap } = this.props;
    const contentModule = contentModuleMap[selectedContentModule.id] ? _.cloneDeep(contentModuleMap[selectedContentModule.id]) : {}
    this.state = {
      alertMessage: ''
      , formHelpers: {
        doesCertExpire: contentModule.certificateDuration ? 'yes' : 'no'
        , isNewConclusionContent: true
        , isNewOverviewContent: true
        , isTestable: contentModule.testable ? 'yes' : 'no'
        , requiresAttestation: contentModule.requiresAttestation ? 'yes' : 'no'
        , showSentimentModal: contentModule.showSentimentModal 
      }
      , isAlertModalOpen: false
      , contentModule: contentModule
      // NOTE: we don't want to change the store, just make changes to a copy
    }
    this._bind(
      '_handleFormChange'
      , '_handleFormSubmit'
      , '_toggleAlertModal'
    );
  }

  componentDidMount() {
    const { dispatch, match } = this.props;
    dispatch(certificateTemplateActions.fetchListIfNeeded('all'))
    dispatch(contentModuleActions.fetchSingleIfNeeded(match.params.contentModuleId)).then((json) => {
      dispatch(sponsorActions.fetchListIfNeeded('all'));
      dispatch(studyActions.fetchListIfNeeded("_organization", json.item._organization));
    });
    dispatch(evaluationActions.fetchListIfNeeded('_contentModule', match.params.contentModuleId ));
  }

  componentWillReceiveProps(nextProps) {
    const { selectedContentModule, contentModuleMap } = nextProps;
    const contentModule = contentModuleMap[selectedContentModule.id] ? { ...contentModuleMap[selectedContentModule.id] } : {}
    this.setState({
      alertMessage: ''
      , formHelpers: {
        doesCertExpire: contentModule.certificateDuration ? 'yes' : 'no'
        , isNewConclusionContent: true
        , isNewOverviewContent: true
        , isNewPostEvalContent: true
        , isTestable: contentModule.testable ? 'yes' : 'no'
        , requiresAttestation: contentModule.requiresAttestation ? 'yes' : 'no'

      }
      , isAlertModalOpen: false
      , contentModule: contentModule
      // we don't want to actually change the store's contentModule, just use a copy
    })
  }

  _handleFormChange(e) {
    /**
     * This let's us change arbitrarily nested objects with one pass
     */

    let newState = _.update(this.state, e.target.name, function() {
      return e.target.value;
    });
    if(e.target.name === "contentModule.allowAnonymous") {
      newState.formHelpers.isTestable = 'no';
      newState.formHelpers.requiresAttestation = 'no';
    }
    if(e.target.name === "contentModule.allowSimFeedback") {
      newState.contentModule.published = !e.target.value;
    }
    // console.log(newState);
    this.setState(newState);

    const { dispatch } = this.props;
    /**
     * TODO:  Change the way this works.  Dispatching this action causes componentWillReceiveProps() to fire
     *        which resets the content module object back to it's original state from the reducer. This means
     *        it's impossible to update the contentModule._organization 
     */
    if(e.target.name === "contentModule._organization") {
      dispatch(studyActions.fetchListIfNeeded("_organization", e.target.value));
    }
  }


  _handleFormSubmit(e) {
    e.preventDefault();
    const { dispatch, history, match } = this.props;
    const { formHelpers } = this.state;
    let newContentModule = _.cloneDeep(this.state.contentModule);
    newContentModule.testable = (formHelpers.isTestable === 'yes');
    newContentModule.requiresAttestation = (formHelpers.requiresAttestation === 'yes');
    newContentModule.showSentimentModal = formHelpers.showSentimentModal; 

    if(formHelpers.doesCertExpire === 'no') {
      newContentModule.certificateDuration = null;
    }
    dispatch(contentModuleActions.sendUpdateContentModule(newContentModule)).then((action) => {
      if(action.success) {
        history.push(`/admin/authoring/content-modules/${match.params.contentModuleId}`)
      } else {
        // console.log("Response Error:");
        // console.log(action);
        // alert("ERROR: "+action.error);
        this._toggleAlertModal(action.error);
      }
    });
  }

  _toggleAlertModal(message) {
    this.setState({
      alertMessage: message || ''
      , isAlertModalOpen: !this.state.isAlertModalOpen
    });
  }

  render() {
    const {
      certificateTemplateStore
      , evaluationStore
      , location
      , match
      , selectedContentModule
      , sponsorStore
      , studyStore
    } = this.props;
    const { formHelpers, contentModule } = this.state;
    const certificateTemplates = certificateTemplateStore.util.getList('all');
    const isEmpty = (!contentModule || contentModule._id === null || contentModule._id === undefined);
    
    const evaluations = evaluationStore.util.getList('_contentModule', match.params.contentModuleId);
    let partitionedEvals = []
    if(evaluations) {
      /**
       * split evaluations into two groups, 'archived' and 'NOT archived'
       * if the 'NOT archived' list is > 0, then don't let the user create any
       * more evaluations.
       * partitionedEvals[0] === 'archived' i.e. {status: 'archived'} is truthy
       * partitionedEvals[1] === 'NOT archived'
       */
      partitionedEvals = _.partition(evaluations, {status: 'archived'})
    }

    return  (
      <AuthoringLayout>
        <div className="flex">
          <Helmet
            title={"Update Content Module | Pro-ficiency"}
          />
          <section className="section">
            <div className="yt-container">
              <Breadcrumbs links={location.state.breadcrumbs} />
            </div>
            { isEmpty ?
              (selectedContentModule.isFetching ? <h2>Loading...</h2> : <h2>Empty.</h2>)
              :
              <ContentModuleForm
                cancelLink={`/admin/authoring/content-modules/${selectedContentModule.id}`}
                certificateTemplates={certificateTemplates}
                formHelpers={formHelpers}
                formTitle="Update Content Module"
                formType="update"
                handleFormChange={this._handleFormChange}
                handleFormSubmit={this._handleFormSubmit}
                sponsorStore={sponsorStore}
                studyStore={studyStore}
                contentModule={contentModule}
                hasActiveEvals={partitionedEvals[1] && partitionedEvals[1].length > 0}
              />
            }
          </section>
          <AlertModal
            alertMessage={this.state.alertMessage}
            alertTitle={"Error!"}
            closeAction={this._toggleAlertModal}
            confirmAction={this._toggleAlertModal}
            confirmText={"Try Again"}
            isOpen={this.state.isAlertModalOpen}
            type="danger"
          />
        </div>
      </AuthoringLayout>
    )
  }
}

UpdateContentModule.propTypes = {
  dispatch: PropTypes.func.isRequired
}

const mapStoreToProps = (store) => {
  /**
  * NOTE: Yote refer's to the global Redux 'state' as 'store' to keep it mentally
  * differentiated from the React component's internal state
  */
  return {
    certificateTemplateStore: store.certificateTemplate
    , evaluationStore: store.evaluation
    , selectedContentModule: store.contentModule.selected
    , sponsorStore: store.sponsor
    , studyStore: store.study
    , contentModuleMap: store.contentModule.byId
  }
}

export default withRouter(
  connect(
    mapStoreToProps
  )(UpdateContentModule)
);



// WEBPACK FOOTER //
// modules/contentModule/views/AdminUpdateContentModule.js.jsx